/**
 * Doomsworn — PILLAR I: PACTS. Power now, debt later.
 *
 * Pacts never fail. You do not roll to see whether the sorcery works. You roll
 * to see WHAT IT COSTS. Every line of the Reckoning table produces the effect;
 * they differ only in the price. A missed Reckoning is not a failed spell — it
 * is a successful spell you could not afford.
 *
 * There is no spell list. A Draw is declared as VERB on DOMAIN at TIER:
 * `Unmake · Flesh · T2`. The Power's domain bounds what it can do, the Tier
 * bounds the scale, the GM adjudicates the fiction, and the engine enforces
 * the cost.
 *
 * Engine authority: Debt totals, Lien existence and satisfaction, and Calling
 * status are engine state. The narrator reads them and cannot write them.
 */
import { tiltPactModifier } from './tilt.js';
export const PACT_TIER_SCALE = {
    1: 'Personal — one body, one object, one moment',
    2: 'Local — a room, a fight, an hour',
    3: 'Regional — a district, a night, a crowd',
    4: 'Mythic — a city, a season, a bloodline',
    5: 'World — the kind of thing that gets a name and a date',
};
/** Debt thresholds at which a Power issues a Calling. */
export const CALLING_THRESHOLDS = [10, 20, 35, 50];
export function crossedCallingThreshold(before, after) {
    for (const t of CALLING_THRESHOLDS) {
        if (before < t && after >= t)
            return t;
    }
    return null;
}
// ---------------------------------------------------------------------------
// THE RECKONING
// ---------------------------------------------------------------------------
/** `DC = 10 + (Tier × 3) + floor(Debt to this Power ÷ 5)` */
export function reckoningDC(tier, debtToThisPower) {
    return 10 + tier * 3 + Math.floor(debtToThisPower / 5);
}
/**
 * Roll a Reckoning.
 *
 * | Result       | Outcome                                                          |
 * |--------------|------------------------------------------------------------------|
 * | Beat the DC  | Debt += Tier                                                     |
 * | Miss         | Debt += Tier × 2, and the Power takes a Lien                     |
 * | Natural 20   | Debt −Tier, or it volunteers something (a boon with an agenda)   |
 * | Natural 1    | The Power manifests. It is on the map. It has a turn.            |
 *
 * Reading of the natural 1 row, made explicit because the table does not spell
 * it out: the Power arriving in person IS the collection, so the Draw costs
 * Tier × 2 in Debt but takes no Lien — it has come to take things itself.
 */
export function resolveReckoning(input) {
    const rng = input.rng ?? Math.random;
    const natural = Math.floor(rng() * 20) + 1;
    const tiltModifier = input.tilt === undefined ? 0 : tiltPactModifier(input.tilt, input.powerSide);
    const total = natural + input.attunementMod + tiltModifier;
    const dc = reckoningDC(input.tier, input.debt);
    const tiltDelta = input.powerSide === 'chaos' ? input.tier : -input.tier;
    const base = {
        effectHappens: true,
        natural,
        total,
        dc,
        tiltModifier,
        tiltDelta,
    };
    if (natural === 20) {
        return {
            ...base,
            outcome: 'pleased',
            debtDelta: -input.tier,
            takesLien: false,
            manifests: false,
            volunteersBoon: true,
            description: `The Power is pleased. It forgives ${input.tier} Debt, or volunteers something instead — a boon with an agenda attached.`,
        };
    }
    if (natural === 1) {
        return {
            ...base,
            outcome: 'manifest',
            debtDelta: input.tier * 2,
            takesLien: false,
            manifests: true,
            volunteersBoon: false,
            description: 'The Power manifests. It is on the map. It has a turn, and it has come to collect in person.',
        };
    }
    if (total >= dc) {
        return {
            ...base,
            outcome: 'paid',
            debtDelta: input.tier,
            takesLien: false,
            manifests: false,
            volunteersBoon: false,
            description: `The Draw lands and the account is charged: Debt +${input.tier} (${total} vs DC ${dc}).`,
        };
    }
    return {
        ...base,
        outcome: 'lien',
        debtDelta: input.tier * 2,
        takesLien: true,
        manifests: false,
        volunteersBoon: false,
        description: `The Draw lands — it always does — but you could not afford it: Debt +${input.tier * 2} and the Power takes a Lien (${total} vs DC ${dc}).`,
    };
}
/** Apply a resolved Reckoning to a ledger entry. Pure; caller persists. */
export function applyReckoning(entry, result) {
    return {
        ...entry,
        debt: Math.max(0, entry.debt + result.debtDelta),
    };
}
//# sourceMappingURL=pacts.js.map